import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { WritableDraft } from 'immer/dist/internal';
import { isMobile } from 'react-device-detect';
import { TypedUseSelectorHook } from 'react-redux';
import { createSelector } from 'reselect';
import { v4 } from 'uuid';

import { RootState, store, useAppSelector } from '../../../App/store';
import { mapDispatchSet } from '../../../utils/redux';
import newFormReducers, { FormVariables } from './newFormReducers';

export type InitialState = typeof initialState;

type UUID = string;
type FormVariablesSet = {
  [key: UUID]: FormVariables;
};

const stateSaveKey = "newFormVariables-state";
const savedFormVariables: FormVariablesSet = (() => {
  try {
    const serializedState = localStorage.getItem(stateSaveKey);

    if (serializedState) return JSON.parse(serializedState);
  } catch (err) {
    console.error(err);
  }

  return {};
})();

const initialState = {
  isLoading: false,
  editingNewFormUUID: "",
  newForms: {} as FormVariablesSet,
};

export const formsSlice = createSlice({
  name: "forms",
  initialState: { ...initialState, ...savedFormVariables },
  reducers: {
    ...newFormReducers,
    setForm: (
      state,
      { payload }: PayloadAction<{ uuid: string; data: FormVariablesSet }>
    ) => {
      state.newForms[payload.uuid] = payload.data;
    },
    createNewForm: (state, action: PayloadAction<string | undefined>) => {
      const uuid = action.payload || v4();
      state.newForms[uuid] = {
        UUID: uuid,
        created: Date.now(),
        editorWindowId: window.windowId,
        isCreatedOnMobile: isMobile,
        employee: {
          name: "Varsayılan",
          id: "unknown",
          pin: "0000",
        },
      };
      state.editingNewFormUUID = uuid;
    },
    focusOnNewForm: (state, action: PayloadAction<string>) => {
      if (!state.newForms[action.payload]) throw "invalid newForm uuid";
      state.editingNewFormUUID = action.payload;
    },
    deleteNewForm: (state, action: PayloadAction<string | undefined>) => {
      delete state.newForms[action.payload || state.editingNewFormUUID];

      const newFormKeys = Object.keys(state.newForms);
      state.editingNewFormUUID = newFormKeys[newFormKeys.length - 1] || "";
    },
    setVariable: <T extends keyof InitialState>(
      state: WritableDraft<InitialState>,
      action: PayloadAction<[T, InitialState[T]]>
    ) => {
      const [key, value] = action.payload;
      state[key] = value;
    },
  },
});

export const saveState = (state: RootState) => {
  try {
    const serializedState = JSON.stringify(state.currentForm);
    localStorage.setItem(stateSaveKey, serializedState);
  } catch (e) {
    console.error("FormStateSave Error:", e);
  }
};

export const actions = mapDispatchSet(formsSlice.actions);
export const getFormVariables = () => {
  const state = store.getState();
  return state.currentForm.newForms[state.currentForm.editingNewFormUUID];
};
export const useFormSelector: TypedUseSelectorHook<FormVariables> = (query) =>
  useAppSelector((state) =>
    query(state.currentForm.newForms[state.currentForm.editingNewFormUUID])
  );
export const createFormSelector = <F, V>(
  a: (arg: FormVariables) => F,
  b: (arg: F) => V
) =>
  createSelector(
    (state: RootState) =>
      a(state.currentForm.newForms[state.currentForm.editingNewFormUUID]),
    b
  );
